import { ObservableService } from '@skinsoft/rxstate'
import {
    animationFrameScheduler,
    BehaviorSubject,
    EMPTY,
    Observable,
    race,
    SchedulerLike,
    Subscription,
    throwError,
} from 'rxjs';
import { catchError, first, last, map, switchMapTo } from 'rxjs/operators'

import { AuthentificationMethod, User } from '@skinsoft/types'
import { fetch, Proxy, API_ENDPOINTS } from '@skinsoft/network'
import { AuthContext, AuthEvent, AuthStateSchema, createMachine } from './machine'

const PROXY = new Proxy()

declare const APP_NAME: string

declare const config
const SSO_TIMEOUT_DURATION: number = config.sso.timeOutDuration
const USER_POLLING_HTTP_CODES_LOGOUT: number[] = [401, 498]

export interface UserAuthenticatedResult {
    user: User,
    authMethod: 'SSO' | 'TRYPHON'
}

export class AuthService extends ObservableService<AuthContext, AuthEvent, AuthStateSchema> {
    public static getInstance(
        storage: Storage = window.localStorage,
        proxy = new Proxy(false),
        scheduler?: SchedulerLike,
    ) {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService(storage, proxy, scheduler);
        }

        return AuthService.instance;
    }

    public static install(Vue) {
        Vue.prototype.$authService = AuthService.getInstance(
            window.localStorage,
            new Proxy(false),
            animationFrameScheduler,
        );
    }

    private static instance: AuthService = null as any;

    public readonly user$ = new BehaviorSubject<UserAuthenticatedResult | null>(null);

    private proxy: Proxy;
    private storage: Storage;
    private userSubscription: Subscription;

    constructor(
        storage: Storage = window.localStorage,
        proxy = new Proxy(false),
        scheduler?: SchedulerLike,
    ) {
        super(
            createMachine({
                beginChallenge: (_, event: AuthEvent) => this.beginChallenge(event),
                clearStorage: () => this.clearStorage(),
                startUserPolling: (context: AuthContext) => this.startUserPolling(context),
                stopUserPolling: () => this.stopUserPolling(),
                storeUserId: (context: AuthContext, event: AuthEvent) => {
                    const { userId } = event.query as { userId: string };
                    context.userId = userId;
                },
                authenticateSSO: (context: AuthContext, event: AuthEvent) => this.authenticateSSO(context, event),
            }),
            scheduler,
        );

        this.storage = storage;
        this.proxy = proxy;
    }

    public async logIn(username: string, password: string) {
        this.sendEvent({ type: 'CHALLENGE', query: { username, password } });
        await this.waitFor$('challenging').toPromise();
    }

    public logOut(error?: Error) {
        this.sendEvent({ type: 'DEAUTHENTICATE', query: { error } });
    }

    public waitForUser$() {
        return race(this.waitFor$('authenticated'), this.waitFor$('unauthenticated')).pipe(
            first(),
            switchMapTo(this.user$),
        );
    }

    protected boot() {
        const ssoData = window.sessionStorage.getItem('ssoUserData')
        if(ssoData) {
            this.sendEvent({ type: 'CHALLENGE_SSO', query: { fromSSO: true } })
            return
        }

        const token = this.storage.getItem('token');
        const userId = this.storage.getItem('userId');

        if (token && userId) {
            fetch.registerToken(token);
            this.sendEvent({ type: 'AUTHENTICATE', query: { userId } });
            return;
        }

        this.sendEvent('DEAUTHENTICATE');
    }

    private beginChallenge(event: AuthEvent) {
        const login$ = this.proxy.fetch('/login', undefined, 'POST', event.query)

        login$.subscribe({
            error: error => {
                this.sendEvent({ type: 'DEAUTHENTICATE', query: { error } })
            },
            next: (loginResponse) => {
                this.storage.setItem('token', loginResponse.token)
                this.storage.setItem('userId', loginResponse.userId)
                this.storage.setItem('institution', loginResponse.institutionId)
                this.storage.setItem('workspace', loginResponse.currentWorkspaceId)

                fetch.registerToken(loginResponse.token)

                this.sendEvent({ type: 'AUTHENTICATE', query: { userId: loginResponse.userId } })
            },
        });
    }

    private authenticateSSO(context: AuthContext, event: AuthEvent) {
        if(event.type === "CHALLENGE_SSO") {
            const ssoUserData = window.sessionStorage.getItem('ssoUserData')
            if(ssoUserData === null) {
                this.sendEvent({ type: 'DEAUTHENTICATE' }) // pass to a state SSO_FAILED
                window.sessionStorage.removeItem('ssoUserData')
                return
            }
            context.fromSSO = true

            const parsedUserData = JSON.parse(ssoUserData)
            // TODO check that parsedUserData is valid
               
            // remove userData from session and put them in localStorage
            window.sessionStorage.removeItem('ssoUserData')
            fetch.registerToken(parsedUserData.token)
               
            setTimeout(() => {
                this.storage.setItem('token', parsedUserData.token)
                this.storage.setItem('userId', parsedUserData.userId)
                this.storage.setItem('preferredLocale', parsedUserData.userDefaultInterfaceLanguage)
                this.storage.setItem('dateFormat', parsedUserData.formatDateEn)
                this.storage.setItem('institution', parsedUserData.institutionId)
                this.storage.setItem('workspace', parsedUserData.currentWorkspaceId)
            }, SSO_TIMEOUT_DURATION)

            this.sendEvent({ type: 'AUTHENTICATE', query: { userId: parsedUserData.userId } })
        }
    }

    public getAuthentificationMethods(): Observable<AuthentificationMethod[]> {
        return this.proxy.fetch(`/application-information/public/authentication-methods/${APP_NAME}`)
    }

    private startUserPolling(context: AuthContext) {
        const uri = `${API_ENDPOINTS.user}/${context.userId}` 
        const user$ = PROXY.fetch(uri, undefined, undefined, undefined, true).pipe(
            last(),
            catchError(error => {
                if(fetch.isNetworkError(error)) {
                    return EMPTY
                }
                
                if(error.status && USER_POLLING_HTTP_CODES_LOGOUT.includes(error.status)) {
                    this.logOut()
                }

                return throwError(error)
            }),
            map(user => {
                return {
                    user: user,
                    authMethod: context.fromSSO ? 'SSO' : 'TRYPHON'
                } as UserAuthenticatedResult
            })
        )
        this.userSubscription = user$.subscribe(user => this.user$.next(user));
    }

    private stopUserPolling() {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }

        this.user$.next(null);
    }

    private clearStorage() {
        this.storage.removeItem('token');
        this.storage.removeItem('userId');
        this.storage.removeItem('workspace');
        this.storage.removeItem('institution');
        fetch.registerToken(null);
    }
}
